export type HitgroupKey =
  | 'head'
  | 'chest'
  | 'stomach'
  | 'left arm'
  | 'right arm'
  | 'left leg'
  | 'right leg';

export type HitgroupValues = Record<HitgroupKey, number>;

export const HITGROUPS: { key: HitgroupKey; label: string }[] = [
  { key: 'head', label: 'Head' },
  { key: 'chest', label: 'Chest' },
  { key: 'stomach', label: 'Stomach' },
  { key: 'left arm', label: 'Left arm' },
  { key: 'right arm', label: 'Right arm' },
  { key: 'left leg', label: 'Left leg' },
  { key: 'right leg', label: 'Right leg' },
];

export const createEmptyHitgroupValues = (): HitgroupValues => ({
  head: 0,
  chest: 0,
  stomach: 0,
  'left arm': 0,
  'right arm': 0,
  'left leg': 0,
  'right leg': 0,
});

type DamageBodySvgProps = {
  values: HitgroupValues;
  maxValue: number;
  baseColor: string;
};

const buildFill = (value: number, maxValue: number, baseColor: string) => {
  const intensity = maxValue > 0 ? value / maxValue : 0;
  const alpha = 0.15 + intensity * 0.85;
  return `rgba(${baseColor}, ${alpha.toFixed(2)})`;
};

export default function DamageBodySvg({ values, maxValue, baseColor }: DamageBodySvgProps) {
  const stroke = 'rgba(246, 241, 232, 0.35)';

  return (
    <svg
      viewBox="0 0 140 220"
      className="h-[240px] w-full max-w-[260px]"
      role="img"
      aria-label="Body hitgroup map"
    >
      <circle
        cx="70"
        cy="24"
        r="16"
        fill={buildFill(values.head, maxValue, baseColor)}
        stroke={stroke}
        strokeWidth="2"
      >
        <title>{`Head: ${Math.round(values.head)} shots`}</title>
      </circle>
      <rect
        x="50"
        y="44"
        width="40"
        height="34"
        rx="10"
        fill={buildFill(values.chest, maxValue, baseColor)}
        stroke={stroke}
        strokeWidth="2"
      >
        <title>{`Chest: ${Math.round(values.chest)} shots`}</title>
      </rect>
      <rect
        x="50"
        y="82"
        width="40"
        height="34"
        rx="10"
        fill={buildFill(values.stomach, maxValue, baseColor)}
        stroke={stroke}
        strokeWidth="2"
      >
        <title>{`Stomach: ${Math.round(values.stomach)} shots`}</title>
      </rect>
      <rect
        x="20"
        y="52"
        width="20"
        height="70"
        rx="8"
        fill={buildFill(values['left arm'], maxValue, baseColor)}
        stroke={stroke}
        strokeWidth="2"
      >
        <title>{`Left arm: ${Math.round(values['left arm'])} shots`}</title>
      </rect>
      <rect
        x="100"
        y="52"
        width="20"
        height="70"
        rx="8"
        fill={buildFill(values['right arm'], maxValue, baseColor)}
        stroke={stroke}
        strokeWidth="2"
      >
        <title>{`Right arm: ${Math.round(values['right arm'])} shots`}</title>
      </rect>
      <rect
        x="52"
        y="120"
        width="18"
        height="72"
        rx="8"
        fill={buildFill(values['left leg'], maxValue, baseColor)}
        stroke={stroke}
        strokeWidth="2"
      >
        <title>{`Left leg: ${Math.round(values['left leg'])} shots`}</title>
      </rect>
      <rect
        x="70"
        y="120"
        width="18"
        height="72"
        rx="8"
        fill={buildFill(values['right leg'], maxValue, baseColor)}
        stroke={stroke}
        strokeWidth="2"
      >
        <title>{`Right leg: ${Math.round(values['right leg'])} shots`}</title>
      </rect>
    </svg>
  );
}
